import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getConfig } from "../config";
import { apiFetch } from "../api/apiFetch";
import { ApiError, type AuthorizationResult } from "../types";
import { Spinner } from "../components/Spinner";
import { AccessDenied, ServiceError, InternalNoGroup, InternalInvalidGroup, GroupPicker } from "./GroupGuardPages";

interface GroupGuardProps {
  children: ReactNode;
}

/**
 * After authentication, checks group/opportunity authorization via the API.
 * Accepts oppId (primary) or groupId (fallback) from the URL.
 */
export function GroupGuard({ children }: GroupGuardProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oppId = searchParams.get("oppId");
  const groupId = searchParams.get("groupId");
  const hasIdentifier = !!oppId || !!groupId;

  const config = getConfig();

  const [authResult, setAuthResult] = useState<AuthorizationResult | null>(
    config.AUTH_ENABLED ? null : { isAuthorized: true, role: "Internal", groups: [] }
  );
  const [authError, setAuthError] = useState<"denied" | "service" | null>(null);
  const fetchedRef = useRef(!config.AUTH_ENABLED);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Build query: oppId takes precedence over groupId
    const params = new URLSearchParams();
    if (oppId) params.set("oppId", oppId);
    else if (groupId) params.set("groupId", groupId);
    const query = params.toString() ? `?${params}` : "";

    apiFetch<AuthorizationResult>(`/employer-intake/authorize${query}`, {
      allowedStatuses: [403],
    })
      .then((result) => {
        if (!result || typeof result.isAuthorized !== "boolean") {
          setAuthError("service");
          return;
        }
        setAuthResult(result);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) {
          // 403 carries an AuthorizationResult body (e.g. user has groups but not this one).
          const body = err.body as AuthorizationResult | undefined;
          if (body && typeof body.isAuthorized === "boolean") {
            setAuthResult(body);
            return;
          }
          setAuthError("denied");
          return;
        }
        setAuthError("service");
      });
  }, [oppId, groupId]);

  const state = useMemo<
    | { status: "loading" }
    | { status: "authorized" }
    | { status: "denied" }
    | { status: "service-error" }
    | { status: "internal-no-group" }
    | { status: "internal-invalid-group" }
    | { status: "picker"; groups: AuthorizationResult["groups"]; message?: string }
  >(() => {
    if (authError === "service") return { status: "service-error" };
    if (authError === "denied") return { status: "denied" };
    if (!authResult) return { status: "loading" };

    if (!authResult.isAuthorized) {
      if (authResult.role === "Internal") return { status: "internal-invalid-group" };
      if (authResult.groups.length > 0) {
        return {
          status: "picker",
          groups: authResult.groups,
          message: hasIdentifier
            ? "The requested group could not be found or you don't have access. Please select one below."
            : undefined,
        };
      }
      return { status: "denied" };
    }

    if (authResult.role === "Internal") {
      return hasIdentifier ? { status: "authorized" } : { status: "internal-no-group" };
    }

    if (authResult.groups.length === 0) return { status: "denied" };

    // If the user came with an oppId or groupId that's authorized, let them through
    if (hasIdentifier) {
      // Check if the resolved account matches one of the authorized groups
      if (oppId && authResult.oppId) return { status: "authorized" };
      if (groupId) {
        const hasAccess = authResult.groups.some((g) => g.accountId === groupId);
        if (hasAccess) return { status: "authorized" };
      }
      return {
        status: "picker",
        groups: authResult.groups,
        message: "You don't have access to the requested group. Please select one below.",
      };
    }

    return { status: "picker", groups: authResult.groups };
  }, [authResult, authError, hasIdentifier, oppId, groupId]);

  // Auto-redirect for single group when no identifier in URL
  useEffect(() => {
    if (
      authResult?.isAuthorized &&
      authResult.role !== "Internal" &&
      authResult.groups.length === 1 &&
      !hasIdentifier
    ) {
      const group = authResult.groups[0];
      const targetOppId = group.oppId;
      if (targetOppId) {
        navigate(`/intake?oppId=${encodeURIComponent(targetOppId)}`, { replace: true });
      } else {
        navigate(`/intake?groupId=${encodeURIComponent(group.accountId)}`, { replace: true });
      }
    }
  }, [authResult, hasIdentifier, navigate]);

  if (state.status === "loading") return <Spinner overlay />;
  if (state.status === "service-error") return <ServiceError />;
  if (state.status === "denied") return <AccessDenied />;
  if (state.status === "internal-no-group") return <InternalNoGroup />;
  if (state.status === "internal-invalid-group") return <InternalInvalidGroup />;
  if (state.status === "picker") return <GroupPicker groups={state.groups} message={state.message} />;

  return children;
}
